import { Button } from '@carbon/react';
import { Maximize } from '@carbon/react/icons';
import { ExpandableContainer } from 'components/expandable-container';
import type { ChartExportDescriptor } from '../chart-export-registry';
import type { ChartSpec, ChartToggle } from './types';
import { SpecChart } from './spec-chart';
import { CustomChart } from './custom-chart';
import styles from './chart-card.module.scss';

/**
 * Placeholder body for chart types whose real chart isn't built yet — keeps the card
 * chrome (header + expand) usable across all catalog entries.
 *
 * @param {object} props - The `title` used in the placeholder copy.
 * @return {JSX.Element} The placeholder body.
 */
function ChartPlaceholder({ title }: { title: string }) {
  return (
    <div className={styles.placeholder}>
      <p>{title} chart coming soon.</p>
    </div>
  );
}

/**
 * Picks the chart body from the instance `source`: a `custom` instance authors its own spec
 * live, so it always renders the interactive `CustomChart` (seeded from its persisted spec);
 * every other (catalog) chart carries a `spec` and renders through the generic `SpecChart` —
 * no per-chart-type switch, no bespoke component. A chart with no spec (shouldn't happen for a
 * real catalog entry) falls back to the placeholder.
 *
 * @param {object} props - The `source`, the `title` (for the placeholder), the `spec` / `toggle`, and the custom-only `onSpecChange`.
 * @return {JSX.Element} The chart body.
 */
function ChartBody({
  source,
  title,
  spec,
  toggle,
  onSpecChange,
  registerExport,
}: {
  source: 'catalog' | 'custom';
  title: string;
  spec?: ChartSpec;
  toggle?: ChartToggle;
  onSpecChange?: (spec: ChartSpec) => void;
  registerExport?: ChartExportDescriptor;
}) {
  if (source === 'custom') {
    return (
      <CustomChart
        initialSpec={spec}
        onSpecChange={onSpecChange}
        registerExport={registerExport}
      />
    );
  }
  if (spec) {
    return (
      <SpecChart spec={spec} toggle={toggle} registerExport={registerExport} />
    );
  }
  return <ChartPlaceholder title={title} />;
}

/**
 * Reusable chart card — the common chrome for every chart in the Charts & Visuals grid:
 * a grey title bar with a Maximize action, and the chart body below. It renders its chart
 * via `ChartBody`, which keys off the instance `source` (custom builder vs generic
 * `SpecChart`).
 *
 * Expand/collapse is delegated to the shared `ExpandableContainer` (the same component the
 * type-curve charts use): clicking Maximize expands the whole card into a bottom slide-up
 * overlay (Esc / backdrop / × to close), where the container supplies its own close
 * button so the header's Maximize is hidden. The card is rendered by the container's
 * render prop, so the chart body remounts on expand — the same behaviour as the
 * type-curve chart cells.
 *
 * @param {object} props - The `source`, the `title` shown in the header, the `spec` / `toggle`, and the custom-only `onSpecChange`.
 * @return {JSX.Element} The chart card.
 */
export function ChartCard({
  source,
  title,
  spec,
  toggle,
  onSpecChange,
  registerExport,
}: {
  source: 'catalog' | 'custom';
  title: string;
  /** Catalog spec (re-resolved) or a custom chart's persisted authored spec. */
  spec?: ChartSpec;
  toggle?: ChartToggle;
  /** Custom charts only: persist the builder's edited (filter-free) spec back to the instance. */
  onSpecChange?: (spec: ChartSpec) => void;
  /** When set, the chart's SciChart surface registers for content export (see {@link ChartExportDescriptor}). */
  registerExport?: ChartExportDescriptor;
}) {
  return (
    <ExpandableContainer expandedClassName={styles.expandedCard}>
      {({ isExpanded, expand }) => (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <span className={styles.cardTitle}>{title}</span>
            {!isExpanded && (
              <div className={styles.cardActions}>
                <Button
                  kind="ghost"
                  hasIconOnly
                  size="sm"
                  renderIcon={Maximize}
                  iconDescription="Expand chart"
                  tooltipPosition="bottom"
                  onClick={expand}
                />
              </div>
            )}
          </div>
          <div className={styles.cardBody}>
            <ChartBody
              source={source}
              title={title}
              spec={spec}
              toggle={toggle}
              onSpecChange={onSpecChange}
              registerExport={registerExport}
            />
          </div>
        </div>
      )}
    </ExpandableContainer>
  );
}
